import { useCallback } from "react";
import { FormikProps } from "formik";
import DropdownSelector from "../../../../Common/dropdown-selector";
import PriceInput from "../../../../Common/price-input/price-input.component";
import styles from "./index.module.scss";
import { IProductFormValues } from "../../../../../common/validations/product.validations";
import { CURRENCY_OPTIONS, TAX_RATES, EXCHANGE_RATE } from "../initial-values";
import FormRow from "../form-row";

interface IItem {
  label: string;
  value: string;
}

interface TotalSectionProps extends FormikProps<IProductFormValues> {}

const TotalSection = ({
  values,
  setFieldValue,
}: TotalSectionProps) => {
  const currencyOptions: IItem[] = CURRENCY_OPTIONS;
  const taxRates: IItem[] = TAX_RATES;

  const selectedCurrency = currencyOptions.find(c => c.value === values.currency) || currencyOptions[0];
  const value = values.cost;
  const selectedTaxRate = taxRates.find(t => t.value === values.taxRate) || taxRates[0];
  const usdAmount = values.usdAmount;
  const gbpAmount = values.gbpAmount;
  const exchangeRate = EXCHANGE_RATE;

  const handleUsdChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/[^0-9.]/g, "");
      const numValue = Number.parseFloat(value) || 0;
      setFieldValue('usdAmount', numValue.toFixed(2));
      setFieldValue('gbpAmount', (numValue * exchangeRate).toFixed(2));
    },
    [exchangeRate, setFieldValue],
  );

  const handleGbpChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/[^0-9.]/g, "");
      const numValue = Number.parseFloat(value) || 0;
      setFieldValue('gbpAmount', numValue.toFixed(2));
      setFieldValue('usdAmount', (numValue / exchangeRate).toFixed(2));
    },
    [exchangeRate, setFieldValue],
  );

  const getTaxRateValue = (label: string): number => {
    const match = label.match(/\d+/);
    return match ? Number.parseInt(match[0]) : 10;
  };

  const currentRateValue = getTaxRateValue(selectedTaxRate.label);
  const calculatedAmount = (currentRateValue * 100).toFixed(2);

  const handleTaxRateChange = (item: IItem) => {
    setFieldValue('taxRate', item.value);
  };

  const handleCurrencyChange = (item: IItem) => {
    setFieldValue('currency', item.value);
  };

  const handleValueChange = (newValue: string | number) => {
    setFieldValue('cost', String(newValue));
  };

  const getCurrencySymbol = (curr: string) => {
    const symbols: { [key: string]: string } = {
      GBP: "£",
      USD: "$",
      EUR: "€",
    };
    return symbols[curr] || "£";
  };

  const currency = values.currency;

  return (
    <div className={styles.container}>
      <FormRow label="Cost" htmlFor="cost-input" variant="fieldRow" styles={styles}>
        <div className={styles.inputContainer}>
          <DropdownSelector
            list={currencyOptions}
            selected={selectedCurrency}
            onItemSelected={handleCurrencyChange}
            toggleClassName={styles.currencySelector}
            className={styles.dropdownWrapper}
          >
            <div className={styles.currencyLabel}>
              <span className={styles.currencyText}>
                {selectedCurrency.label}
              </span>
              <div className={styles.arrowContainer}>
                <div className="arrow-icon" />
              </div>
            </div>
          </DropdownSelector>

          <div className={styles.inputWrapper}>
            <PriceInput
              id="cost-input"
              value={value}
              onChange={handleValueChange}
              prefix={getCurrencySymbol(currency)}
              className={styles.input}
              aria-label="Cost amount"
              placeholder="0.00"
            />
          </div>
        </div>
      </FormRow>

      <FormRow label="Tax Rate" htmlFor="tax-rate-select" variant="fieldRow" styles={styles}>
        <div className={styles.taxRateContainer}>
          <DropdownSelector
            list={taxRates}
            selected={selectedTaxRate}
            onItemSelected={handleTaxRateChange}
            toggleClassName={styles.taxRateDropdown}
            className={styles.dropdownWrapper}
          >
            <div className={styles.dropdownButton}>
              <span className={styles.dropdownText}>
                {selectedTaxRate.label}
              </span>
              <div className={styles.taxRateArrowContainer}>
                <div className={styles.taxRateArrowWrapper}>
                  <div className="arrow-icon" />
                </div>
              </div>
            </div>
          </DropdownSelector>

          <output
            htmlFor="tax-rate-select"
            className={styles.taxRateOutput}
          >
            {currentRateValue}% = {getCurrencySymbol(currency)} {calculatedAmount}
          </output>
        </div>
      </FormRow>

      <FormRow
        label="Total"
        htmlFor="usd-input"
        variant="totalRow"
        labelId="currency-exchange-label"
        containerProps={{
          role: "group",
          "aria-labelledby": "currency-exchange-label",
        }}
        styles={styles}
      >
        <div className={styles.totalInputContainer}>
          <div className={styles.totalInputWrapper}>
            <label htmlFor="usd-input" className={styles.srOnly}>
              US Dollar Amount
            </label>
            <input
              id="usd-input"
              type="text"
              inputMode="decimal"
              value={`$ ${usdAmount}`}
              onChange={handleUsdChange}
              className={styles.totalInput}
              aria-label="US Dollar amount"
            />
          </div>

          <img 
            src="/images/msp/exchange-rate-icon.svg"
            alt="exchange-rate-icon"
            className={styles.exchangeIcon}
          />
          <div className={styles.totalInputWrapperRight}>
            <label htmlFor="gbp-input" className={styles.srOnly}>
              British Pound Amount
            </label>
            <input
              id="gbp-input"
              type="text"
              inputMode="decimal"
              value={`£ ${gbpAmount}`}
              onChange={handleGbpChange}
              className={styles.totalInputRight}
              aria-label="British Pound amount"
            />
          </div>
        </div>
      </FormRow>
    </div>
  );
};

export default TotalSection;
